import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  Banner,
  Select,
  Spinner,
  ResourceList,
  ResourceItem,
  Text,
  Avatar,
  Card,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetchCompanies } from "./company-select.slice";
import "./company-select.scss";
import companyChart from "../../assets/images/company-chart.svg";
import ImageMapper from "react-img-mapper";
import { coordinateJson } from "components/orgChart/orgChartData";

function CompanySelect({ onChange, isList, orgChart }) {
  const dispatch = useDispatch();
  const history = useNavigate();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const { companiesCollection, companiesCollectionRaw, isLoading, hasLoaded, isError } =
    useSelector((state) => state.companySelectReducer);

  const handleCompanyRedirect = (area) => {
    if (checkCompanyAccess(area.id)) {
      if (area.id) history(`/app/${area.id}`);
    }
  };
  const validCompnayIds = companiesCollectionRaw.reduce((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {});

  const checkCompanyAccess = (id) => {
    if (validCompnayIds.hasOwnProperty(id)) {
      return true;
    }
    return false;
  };

  const handleCompanyChange = (companyToSelect) => {
    onChange(companyToSelect);
  };

  useEffect(() => {
    if (!companiesCollection.length) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, companiesCollection.length]);

  // Still waiting — including before the first request goes out. That is what
  // the initial state looks like, and it must not be mistaken for an empty
  // result.
  if ((!hasLoaded || isLoading) && companiesCollection.length === 0) {
    return <Spinner accessibilityLabel="" size="small" />;
  }

  if (companiesCollection.length === 0) {
    // Never render nothing. This returned null, so a user whose company list
    // failed to load — or who genuinely has no company yet — got a blank page
    // with no message, no retry and no way to tell the two apart. It is the
    // first thing a newly activated user sees.
    return (
      <Banner
        status={isError ? "critical" : "info"}
        title={isError ? t("companies_load_failed") : t("no_companies_yet")}
        action={{
          content: t("try_again"),
          onAction: () => dispatch(fetchCompanies()),
        }}
      >
        <p>{isError ? t("companies_load_failed_hint") : t("no_companies_yet_hint")}</p>
      </Banner>
    );
  }

  const MAP = {
    name: "my-map",
    areas: coordinateJson,
  };

  const handleMouseEnter = (area) => {
    if (checkCompanyAccess(area.id)) {
      document.body.style.cursor = "pointer";
    }
  };

  const handleMouseLeave = () => {
    document.body.style.cursor = "default";
  };

  if (orgChart) {
    return (
      <div className="company_chart">
        <ImageMapper
          src={companyChart}
          map={MAP}
          onClick={handleCompanyRedirect}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      </div>
    );
  }

  if (isList) {
    return (
      <Card>
        <ResourceList
          resourceName={{ singular: "customer", plural: "customers" }}
          items={companiesCollectionRaw}
          renderItem={(item) => {
            const { id, name, address } = item;
            const media = <Avatar customer size="md" name={name} />;

            return (
              <ResourceItem
                id={id}
                url={id}
                media={media}
                accessibilityLabel={`View details for ${name}`}
              >
                <Text variant="bodyMd" fontWeight="bold" as="h3">
                  {name}
                </Text>
                <div>{address}</div>
              </ResourceItem>
            );
          }}
        />
      </Card>
    );
  }

  return (
    <div className="company-select">
      <Select
        label={t("Selected company")}
        labelInline
        options={companiesCollection}
        onChange={handleCompanyChange}
        value={companyId}
        disabled={isLoading}
      />
    </div>
  );
}

export default CompanySelect;

CompanySelect.propTypes = {
  onChange: PropTypes.func,
};

CompanySelect.defaultProps = {
  onChange: () => {},
};
